import React, { useState } from "react";
import {
  Grid,
  TextField,
  MenuItem,
  IconButton,
  InputAdornment,
  FormHelperText,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

// Passkey type options
const PASSKEY_TYPES = [
  { value: "Login credentials", label: "Login Credentials" },
  { value: "Passcodes", label: "Passcodes" },
];

/**
 * PasskeyFields Component
 * Reusable component for passkey/password input fields
 * 
 * @param {Object} formData - The form data object
 * @param {Function} handleInputChange - Handler for input changes
 * @param {string} prefix - Field name prefix (e.g., 'passkey1_')
 * @param {Object} errors - Validation errors object
 * @param {boolean} disabled - Whether fields are disabled
 */
const PasskeyFields = ({
  formData = {},
  handleInputChange,
  prefix = "",
  errors = {},
  disabled = false,
}) => {
  const [showPin, setShowPin] = useState(false);

  // Extract field values with prefix
  const type = formData[`${prefix}type`] || "";
  const name = formData[`${prefix}name`] || "";
  const description = formData[`${prefix}description`] || "";
  const pin = formData[`${prefix}pin`] || "";

  // Extract errors
  const typeError = errors[`${prefix}type`] || "";
  const nameError = errors[`${prefix}name`] || "";
  const descriptionError = errors[`${prefix}description`] || "";
  const pinError = errors[`${prefix}pin`] || "";

  // Toggle PIN visibility
  const handleTogglePinVisibility = () => {
    setShowPin(!showPin);
  };


  return (
    <Grid container spacing={2}>
      {/* Passkey Type */}
      <Grid item xs={12} sm={6}>
        <TextField
          select
          fullWidth
          label="Type"
          name={`${prefix}type`}
          value={type}
          onChange={handleInputChange}
          disabled={disabled}
          error={!!typeError}
          helperText={typeError || "Select the type of passkey"}
          required
          sx={{ '& .MuiInputBase-root': { minHeight: '56px' } }}
        >
          <MenuItem value="">
            <em>Select Type</em>
          </MenuItem>
          {PASSKEY_TYPES.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      {/* Name/Username */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label={type === "Login credentials" ? "Username" : "Name"}
          name={`${prefix}name`}
          value={name}
          onChange={handleInputChange}
          disabled={disabled}
          error={!!nameError}
          helperText={
            nameError ||
            (type === "Login credentials"
              ? "Enter username or email"
              : "Enter a name for this passkey")
          }
          placeholder={
            type === "Login credentials"
              ? "e.g., user@example.com"
              : "e.g., Main Account"
          }
          required
          sx={{ '& .MuiInputBase-root': { minHeight: '56px' } }}
        />
      </Grid>

      {/* Description */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Description"
          name={`${prefix}description`}
          value={description}
          onChange={handleInputChange}
          disabled={disabled}
          error={!!descriptionError}
          helperText={descriptionError || "Enter a description for this passkey"}
          placeholder="e.g., Work email account"
          required
        />
      </Grid>

      {/* PIN/Password */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label={type === "Passcodes" ? "PIN" : "Password"}
          name={`${prefix}pin`}
          type={showPin ? "text" : "password"}
          value={pin}
          onChange={handleInputChange}
          disabled={disabled}
          error={!!pinError}
          helperText={
            pinError ||
            (type === "Passcodes"
              ? "Enter numeric PIN (will be stored securely using hash)"
              : "Enter password (will be stored securely using hash)")
          }
          placeholder={
            type === "Passcodes"
              ? "e.g., 1234"
              : "Enter a strong password"
          }
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleTogglePinVisibility}
                  edge="end"
                  disabled={disabled}
                  size="small"
                >
                  {showPin ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <FormHelperText sx={{ ml: 2, color: "info.main" }}>
          🔐 {type === "Passcodes" ? "PIN" : "Password"} will be securely encrypted before storage
        </FormHelperText>
      </Grid>
    </Grid>
  );
};

export default PasskeyFields;
